import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ScanLine, Search, Minus, Plus, ArrowLeft, ArrowRight, Check, Filter } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { SkenerSheet } from "./Skener";
import ZakazkaPicker from "./ZakazkaPicker";
import api, { apiError } from "../lib/api";
import Segment from "./Segment";
import Vyber from "./Vyber";

/**
 * Jeden tok pro výdejku, vratku i převodku — typ je přepínač, ne tři formuláře.
 *
 * Pořadí kroků je vždycky stejné: sken → potvrzení co a kolik → seznam →
 * uložení. V celém toku nejsou ceny: dělník na stavbě je nepotřebuje a jen
 * ho zdržují. Hodnota dokladu se dopočítá až v ekonomice.
 */

const TYPY = {
  VYDEJKA: {
    tab: "Výdejka", nadpis: "Výdejka na zakázku", akce: "Vydat",
    ulozit: "Uložit jako výdejku", kolik: "Kolik si beru", cesta: "/api/sklad/vydejka",
  },
  VRATKA: {
    tab: "Vratka", nadpis: "Vratka ze zakázky", akce: "Vrátit na sklad",
    ulozit: "Uložit jako vratku", kolik: "Kolik vracím", cesta: "/api/sklad/vratka",
  },
  PREVODKA: {
    tab: "Převodka", nadpis: "Převodka mezi sklady", akce: "Převést",
    ulozit: "Uložit jako převodku", kolik: "Kolik převádím", cesta: "/api/sklad/presun",
  },
};

const ROZPRACOVANE = "pp-sklad-doklad";

/** 1 položka / 2–4 položky / 5+ položek. „1 položek" je vidět na první pohled. */
function pocet(n, jedna, dve, pet) {
  const cislo = Math.round(n * 100) / 100;
  if (cislo === 1) return `1 ${jedna}`;
  if (cislo >= 2 && cislo <= 4 && Number.isInteger(cislo)) return `${cislo} ${dve}`;
  return `${cislo} ${pet}`;
}

function SkladSelect({ sklady, value, onChange, label, testid }) {
  return (
    <label className="block">
      <span className="label-up text-muted block mb-1.5">{label}</span>
      <Vyber className="field" value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}>
        <option value="">— vyber sklad —</option>
        {sklady.map((s) => (
          <option key={s.id} value={s.id}>{s.nazev}{s.typ === "DODAVKA" ? " (dodávka)" : ""}</option>
        ))}
      </Vyber>
    </label>
  );
}

function PrepinacTypu({ typ, onZmena, tmavy }) {
  return (
    <Segment jezdec={tmavy ? "bg-white shadow-card" : "bg-ink"} className={`flex gap-1 p-1 rounded-pill w-fit ${tmavy ? "bg-white/10" : "bg-track"}`}
      data-testid="prepinac-typu">
      {Object.entries(TYPY).map(([klic, t]) => {
        const aktivni = typ === klic;
        return (
          <button data-on={aktivni} key={klic} onClick={() => onZmena(klic)} data-testid={`typ-${klic}`}
            className={`pill px-4 h-9 text-[13px] font-600 whitespace-nowrap ${
              aktivni ? (tmavy ? "text-ink" : "text-white")
                : (tmavy ? "text-[#c9ced5]" : "text-sec-3")}`}>
            {t.tab}
          </button>
        );
      })}
    </Segment>
  );
}

function Stepper({ hodnota, onZmena, velky, testid }) {
  // Během psaní drží pole vlastní text. Dřív prázdné pole hned poslalo 0 —
  // řádek dokladu se smazal a další číslice se psaly za nulu.
  const [text, setText] = useState(null);
  const odeslat = () => {
    if (text === null) return;
    const v = Number(String(text).replace(",", "."));
    setText(null);
    if (text.trim() !== "" && Number.isFinite(v)) onZmena(Math.max(0, v));
  };
  const krok = (o) => onZmena(Math.max(0, Math.round((Number(hodnota || 0) + o) * 100) / 100));
  const tlacitko = velky ? "h-14 w-14 rounded-[16px]" : "h-7 w-7 rounded-[9px]";
  return (
    <div className={`flex items-center ${velky ? "gap-3" : "gap-2"}`}>
      <button type="button" onClick={() => krok(-1)} data-testid={`${testid}-min`}
        className={`${tlacitko} bg-line-softer text-sec flex items-center justify-center shrink-0`}>
        <Minus size={velky ? 22 : 14} />
      </button>
      <input value={text === null ? hodnota : text} inputMode="decimal" data-testid={testid}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setText(e.target.value)}
        onBlur={odeslat}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className={`font-mono tabular-nums font-700 text-center bg-transparent outline-none text-ink ${
          velky ? "text-[40px] w-[130px]" : "text-[15px] w-[52px]"}`} />
      <button type="button" onClick={() => krok(1)} data-testid={`${testid}-plus`}
        className={`${tlacitko} bg-line-softer text-sec flex items-center justify-center shrink-0`}>
        <Plus size={velky ? 22 : 14} />
      </button>
    </div>
  );
}

export default function SkladDoklad({ sklady, vychozi, onClose, onHotovo }) {
  const [typ, setTyp] = useState(vychozi?.typ || "VYDEJKA");
  const [skladZdroj, setSkladZdroj] = useState(vychozi?.sklad_id || sklady[0]?.id || "");
  const [skladCil, setSkladCil] = useState(
    vychozi?.do_skladu_id || sklady.find((s) => s.id !== (vychozi?.sklad_id || sklady[0]?.id))?.id || "");
  const [pripadId, setPripadId] = useState(vychozi?.obchodni_pripad_id || null);
  const [techId, setTechId] = useState(vychozi?.technologie_id || null);
  const [techPopis, setTechPopis] = useState(vychozi?.technologie_popis || "");
  const [radky, setRadky] = useState([]);
  const [poznamka, setPoznamka] = useState("");

  const [faze, setFaze] = useState("SEZNAM");      // mobil: SKEN | SEZNAM | HOTOVO
  const [skenuje, setSkenuje] = useState(false);   // kamera přes celou obrazovku
  const [karta, setKarta] = useState(null);
  const [kandidati, setKandidati] = useState(null);
  const [zamitnuto, setZamitnuto] = useState(null);
  // Filtr na technologii je doporučení, ne zámek — párování podle ceníku
  // nemusí sedět a skladník musí mít jak ho obejít.
  const [jenTech, setJenTech] = useState(true);
  const [hledani, setHledani] = useState("");
  const [nalezene, setNalezene] = useState([]);
  const [posledniId, setPosledniId] = useState(null);
  const [ulozeno, setUlozeno] = useState(null);
  const [chyba, setChyba] = useState("");
  const [uklada, setUklada] = useState(false);
  const [upravaMista, setUpravaMista] = useState(!vychozi);

  const skenRef = useRef(null);
  const [skenText, setSkenText] = useState("");
  const t = TYPY[typ];

  const jmenoSkladu = (id) => sklady.find((s) => s.id === id)?.nazev || "—";
  const zdrojId = typ === "VRATKA" ? null : skladZdroj;
  const cilovySklad = typ === "VYDEJKA" ? null : (typ === "VRATKA" ? skladZdroj : skladCil);

  // U vratky je strop to, co se na zakázku vydalo; jinak zásoba zdrojového skladu.
  const parametry = useCallback(() => {
    const q = {};
    if (typ === "VRATKA") {
      if (pripadId) q.obchodni_pripad_id = pripadId;
      if (techId) q.technologie_id = techId;
      // Vrátit jde jen to, co se na zakázku vydalo. Bez zakázky není podle
      // čeho filtrovat, tak se nefiltruje — jinak by seznam byl vždy prázdný.
      if (pripadId || techId) q.jen_vydane = 1;
    } else {
      if (skladZdroj) q.sklad_id = skladZdroj;
      // Převádět jde jen to, co na zdrojovém skladu opravdu leží.
      if (typ === "PREVODKA") q.jen_skladem = 1;
      else if (techId) {
        q.technologie_id = techId;
        if (jenTech) q.jen_k_technologii = 1;
      }
    }
    return q;
  }, [typ, pripadId, techId, skladZdroj, jenTech]);

  const strop = useCallback((p) => (typ === "VRATKA" ? (p.na_zakazce ?? 0) : (p.dostupne ?? 0)), [typ]);

  // ---------------------------------------------------------- rozpracované
  useEffect(() => {
    if (vychozi) return;
    try {
      const ulozene = JSON.parse(localStorage.getItem(ROZPRACOVANE) || "null");
      if (!ulozene?.radky?.length) return;
      setTyp(ulozene.typ || "VYDEJKA");
      setSkladZdroj(ulozene.skladZdroj || "");
      setSkladCil(ulozene.skladCil || "");
      setPripadId(ulozene.pripadId || null);
      setTechId(ulozene.techId || null);
      setTechPopis(ulozene.techPopis || "");
      setRadky(ulozene.radky);
      setUpravaMista(false);
    } catch { /* rozbitý draft radši zahodíme */ }
  }, [vychozi]);

  useEffect(() => {
    const data = { typ, skladZdroj, skladCil, pripadId, techId, techPopis, radky };
    if (radky.length) localStorage.setItem(ROZPRACOVANE, JSON.stringify(data));
    else localStorage.removeItem(ROZPRACOVANE);
  }, [typ, skladZdroj, skladCil, pripadId, techId, techPopis, radky]);

  // ---------------------------------------------------------- sken a výběr
  const otevriKartu = (polozka, kod) => {
    const uz = radky.find((r) => r.polozka_id === polozka.id);
    const baleni = polozka.baleni && polozka.baleni > 1 ? polozka.baleni : 1;
    setKandidati(null); setZamitnuto(null);
    setKarta({
      polozka, pridatKod: kod || null, uz: !!uz,
      // U položek s balením se nabízí celé balení — nikdo neodpočítává svazek po kusech.
      mnozstvi: uz ? uz.mnozstvi : baleni,
    });
  };

  const nactiKod = useCallback(async (kod) => {
    setChyba("");
    try {
      const { data } = await api.get("/api/sklad/najdi", { params: { kod, ...parametry() } });
      // Kód sedí, jen položka neprošla filtrem — to není „neznámý kód",
      // skladníkovi musíme říct proč, ne ho poslat přiřazovat EAN znovu.
      if (!data.nalezeno && data.duvod) setZamitnuto({ duvod: data.duvod, polozka: data.polozka });
      else if (data.nalezeno) otevriKartu(data.polozka);
      else setKandidati({ kod: data.kod, items: data.kandidati || [] });
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail) || e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametry, radky]);

  useEffect(() => {
    if (!hledani.trim()) { setNalezene([]); return undefined; }
    const casovac = setTimeout(async () => {
      try {
        const { data } = await api.get("/api/sklad/najdi", { params: { kod: hledani, ...parametry() } });
        setNalezene(data.nalezeno ? [data.polozka] : (data.kandidati || []));
      } catch { setNalezene([]); }
    }, 250);
    return () => clearTimeout(casovac);
  }, [hledani, parametry]);

  /** Opakovaný sken téže položky přičítá k řádku, nezakládá druhý. */
  const pridej = async () => {
    const mnozstvi = Number(karta.mnozstvi);
    if (!mnozstvi || mnozstvi <= 0) return setChyba("Zadej množství.");
    if (karta.pridatKod) {
      try { await api.patch(`/api/sklad/polozky/${karta.polozka.id}`, { ean: karta.pridatKod }); }
      catch { /* přiřazení kódu je bonus, doklad kvůli němu neblokujeme */ }
    }
    const p = karta.polozka;
    setRadky((r) => {
      const uz = r.find((x) => x.polozka_id === p.id);
      if (uz) return r.map((x) => (x.polozka_id === p.id ? { ...x, mnozstvi } : x));
      return [...r, {
        polozka_id: p.id, nazev: p.nazev, kod: p.kod || "", ean: p.ean || "",
        mj: p.mj || "ks", mnozstvi, skladem: strop(p),
      }];
    });
    setPosledniId(p.id);
    setKarta(null); setChyba(""); setHledani("");
  };

  /** „Přidat i tak" u cizí technologie: filtr shodíme, ať dál nepřekáží. */
  const pridejPresto = () => {
    setJenTech(false);
    otevriKartu(zamitnuto.polozka);
  };

  const upravRadek = (id, mnozstvi) => {
    setPosledniId(id);
    setRadky((r) => (mnozstvi > 0
      ? r.map((x) => (x.polozka_id === id ? { ...x, mnozstvi } : x))
      : r.filter((x) => x.polozka_id !== id)));
  };

  useEffect(() => {
    if (!posledniId) return undefined;
    const casovac = setTimeout(() => setPosledniId(null), 2500);
    return () => clearTimeout(casovac);
  }, [posledniId]);

  const kusu = useMemo(() => radky.reduce((s, r) => s + (r.mnozstvi || 0), 0), [radky]);
  const podZasobou = radky.filter((r) => r.mnozstvi > (r.skladem ?? 0));

  // ---------------------------------------------------------- uložení
  const uloz = async () => {
    if (!radky.length) return setChyba("Načti aspoň jednu položku.");
    if (typ !== "VRATKA" && !skladZdroj) return setChyba("Vyber sklad, ze kterého se vydává.");
    if (typ === "PREVODKA" && !skladCil) return setChyba("Vyber cílový sklad.");
    if (typ === "PREVODKA" && skladZdroj === skladCil) return setChyba("Zdrojový a cílový sklad jsou stejné.");
    if (typ !== "PREVODKA" && !pripadId && !techId) return setChyba("Vyber zakázku.");
    setChyba(""); setUklada(true);
    const polozky = radky.map((r) => ({ polozka_id: r.polozka_id, mnozstvi: r.mnozstvi }));
    try {
      const telo = typ === "PREVODKA"
        ? { z_skladu_id: skladZdroj, do_skladu_id: skladCil, radky: polozky, poznamka, povolit_minus: true }
        : {
          sklad_id: skladZdroj, obchodni_pripad_id: pripadId, technologie_id: techId,
          radky: polozky, poznamka, povolit_minus: true,
        };
      const { data } = await api.post(t.cesta, telo);
      localStorage.removeItem(ROZPRACOVANE);
      setUlozeno({ ...data, odkud: odkudPopis(), kam: kamPopis() });
      setFaze("HOTOVO");
      onHotovo && onHotovo();
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setUklada(false);
    }
  };

  const dalsiDoklad = () => {
    setRadky([]); setUlozeno(null); setPosledniId(null); setFaze("SEZNAM");
  };

  function odkudPopis() {
    return typ === "VRATKA" ? "zakázka" : jmenoSkladu(skladZdroj);
  }
  function kamPopis() {
    if (typ === "VYDEJKA") return "zakázka";
    return jmenoSkladu(typ === "VRATKA" ? skladZdroj : skladCil);
  }

  // ---------------------------------------------------------- klávesnice
  const naKlavesu = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const kod = skenText.trim();
      if (kod) { setSkenText(""); nactiKod(kod); }
      return;
    }
    if (!radky.length) return;
    const posledni = radky[radky.length - 1];
    if (e.key === "+" || e.key === "-") {
      e.preventDefault();
      upravRadek(posledni.polozka_id, posledni.mnozstvi + (e.key === "+" ? 1 : -1));
    }
    if (e.key === "Delete") {
      e.preventDefault();
      upravRadek(posledni.polozka_id, 0);
    }
  };

  // ---------------------------------------------------------- kusy UI
  const misto = (
    <div className="grid gap-3 md:grid-cols-2">
      {typ !== "VRATKA" && (
        <SkladSelect sklady={sklady} value={skladZdroj} onChange={setSkladZdroj}
          label={typ === "PREVODKA" ? "Odkud" : "Sklad"} testid="doklad-sklad-zdroj" />
      )}
      {typ === "PREVODKA" && (
        <SkladSelect sklady={sklady} value={skladCil} onChange={setSkladCil}
          label="Kam" testid="doklad-sklad-cil" />
      )}
      {typ === "VRATKA" && (
        <SkladSelect sklady={sklady} value={skladZdroj} onChange={setSkladZdroj}
          label="Vrátit na sklad" testid="doklad-sklad-cil" />
      )}
      {typ !== "PREVODKA" && (
        <div className="md:col-span-2">
          <ZakazkaPicker pripadId={pripadId} technologieId={techId}
            onChange={(p, tech, popis) => { setPripadId(p); setTechId(tech); setTechPopis(popis || ""); }}
            testid="doklad-zakazka" />
        </div>
      )}
    </div>
  );

  /** Párování podle ceníku nemusí sedět, proto je filtr vypínatelný. */
  const filtrTech = typ === "VYDEJKA" && techId ? (
    <button type="button" onClick={() => setJenTech((v) => !v)} data-testid="doklad-filtr-tech"
      className={`tap pill inline-flex items-center gap-2 px-3.5 h-9 t-meta font-600 border ${
        jenTech ? "bg-[#fff6f0] border-[#ffd9c2] text-accent-hover"
          : "bg-white border-line-strong text-muted"}`}>
      <Filter size={14} className="shrink-0" />
      {jenTech
        ? `Jen materiál k: ${techPopis || "technologii"}`
        : "Filtr technologie vypnutý — nabízím celý sklad"}
    </button>
  ) : null;

  const kontext = (tmavy) => (
    <button onClick={() => setUpravaMista((v) => !v)} data-testid="doklad-kontext"
      className={`font-mono text-[11.5px] flex items-center gap-2 ${tmavy ? "text-ondark-muted" : "text-muted"}`}>
      {odkudPopis()} <ArrowRight size={12} className="text-accent" /> {kamPopis()}
    </button>
  );

  const seznamRadku = (
    <div className="space-y-2" data-testid="doklad-radky">
      {radky.map((r) => {
        const zvyrazneny = posledniId === r.polozka_id;
        const minus = r.mnozstvi > (r.skladem ?? 0);
        return (
          <div key={r.polozka_id} data-testid={`doklad-radek-${r.polozka_id}`}
            className={`rounded-[12px] border px-3.5 py-2.5 flex items-center gap-3 transition-colors ${
              zvyrazneny ? "bg-[#fff6f0] border-[#ffd9c2]" : "bg-white border-line-soft"}`}>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] md:text-[13px] font-600 text-ink truncate">{r.nazev}</span>
              <span className="block font-mono text-[11px] text-muted truncate">
                {[r.kod, r.ean].filter(Boolean).join(" · ") || "bez kódu"}
                {minus ? ` · skladem jen ${r.skladem ?? 0} ${r.mj}` : ""}
              </span>
            </span>
            <Stepper hodnota={r.mnozstvi} onZmena={(v) => upravRadek(r.polozka_id, v)}
              testid={`mnozstvi-${r.polozka_id}`} />
            <span className="font-mono text-[11px] text-muted-2 w-[34px]">{r.mj}</span>
            <button onClick={() => upravRadek(r.polozka_id, 0)} data-testid={`smazat-${r.polozka_id}`}
              className="h-8 w-8 rounded-full bg-line-softer text-muted hover:text-danger flex items-center justify-center shrink-0">
              <X size={15} />
            </button>
          </div>
        );
      })}
      {!radky.length && (
        <div className="rounded-card border border-dashed border-line-strong py-10 text-center text-[13px] text-muted">
          Seznam je prázdný. Načti první kód.
        </div>
      )}
    </div>
  );

  const hledaciPole = (
    <>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input value={hledani} onChange={(e) => setHledani(e.target.value)} data-testid="doklad-hledat"
          placeholder="Hledat podle názvu, EAN nebo katalogového čísla"
          className="field pl-9" />
      </div>
      {nalezene.length > 0 && (
        <div className="border border-line-softer rounded-card overflow-hidden max-h-[240px] overflow-y-auto">
          {nalezene.map((p) => (
            <button key={p.id} type="button" data-testid={`doklad-nalezena-${p.id}`}
              onClick={() => otevriKartu(p)}
              className="w-full text-left flex items-center gap-3 px-3.5 py-2.5 border-b border-line-softer last:border-0 hover:bg-inputbg">
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-600 text-ink truncate">{p.nazev}</span>
                <span className="block font-mono text-[11px] text-muted truncate">
                  {[p.kod, p.ean].filter(Boolean).join(" · ") || "bez kódu"}
                </span>
              </span>
              <span className="font-mono text-[12px] font-700 text-ink shrink-0">
                {strop(p)} {p.mj || "ks"}
              </span>
            </button>
          ))}
        </div>
      )}
      {hledani.trim() && !nalezene.length && (
        <p className="t-meta text-muted" data-testid="doklad-nic-nenalezeno">
          {typ === "VRATKA" ? "Nic takového se na tuhle zakázku nevydalo."
            : typ === "PREVODKA" ? "Na zdrojovém skladu nic takového neleží."
              : jenTech ? "K téhle technologii nic takového nemáme. Filtr jde vypnout výš."
                : "Nic takového na skladě není."}
        </p>
      )}
    </>
  );

  const souhrn = (
    <div className="rounded-card bg-ink text-white px-[18px] py-3.5" data-testid="doklad-souhrn">
      <div className="label-up text-[#8a919a]">Doklad</div>
      <div className="text-[13px] font-600 mt-0.5">
        {pocet(radky.length, "položka", "položky", "položek")} · {pocet(kusu, "kus", "kusy", "kusů")}
      </div>
    </div>
  );

  const varovaniMinus = podZasobou.length > 0 && (
    <div className="card px-4 py-3 flex items-start gap-3" data-testid="doklad-minus">
      <span className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0" />
      <span className="min-w-0">
        <span className="block text-[13px] font-700 text-ink">
          {pocet(podZasobou.length, "položka jde", "položky jdou", "položek jde")} pod evidovanou zásobu
        </span>
        <span className="block text-[12px] text-muted mt-0.5">
          Doklad uložit půjde a bude označený. Sedí to? Jinak nejdřív udělej příjem.
        </span>
      </span>
    </div>
  );

  // ---------------------------------------------------------- potvrzení
  if (faze === "HOTOVO" && ulozeno) {
    return (
      <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center md:p-4"
        data-testid="doklad-hotovo">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white w-full md:max-w-[420px] rounded-t-sheet md:rounded-card shadow-panel p-6 text-center space-y-3">
          <span className="mx-auto h-14 w-14 rounded-full bg-success-bg text-success flex items-center justify-center">
            <Check size={26} strokeWidth={2.4} />
          </span>
          <h2 className="text-[17px] font-800 text-ink">{t.tab} uložena</h2>
          <p className="font-mono text-[12.5px] text-muted">
            {ulozeno.cislo} · {pocet(ulozeno.polozek ?? radky.length, "položka", "položky", "položek")}
          </p>
          <p className="text-[12.5px] text-sec-3">{ulozeno.odkud} → {ulozeno.kam}</p>
          <div className="pt-2 space-y-2">
            <button onClick={dalsiDoklad} data-testid="doklad-dalsi"
              className="w-full h-12 rounded-pill bg-ink text-white text-[14px] font-700">
              Další doklad
            </button>
            <button onClick={onClose} className="w-full text-[12.5px] font-600 text-muted">
              Zobrazit doklad
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------- mobil: hledáček
  if (faze === "SKEN") {
    const posledni = radky[radky.length - 1];
    return (
      <div className="fixed inset-0 z-[60] bg-sidebar-1 flex flex-col md:hidden" data-testid="doklad-sken">
        <div className="flex items-center gap-2 px-3 pt-3">
          <PrepinacTypu typ={typ} onZmena={setTyp} tmavy />
          <button onClick={() => setFaze("SEZNAM")} data-testid="sken-zavrit"
            className="ml-auto tap h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center">
            <X size={20} />
          </button>
        </div>
        <div className="px-4 pt-2 font-mono text-[11px] text-ondark-muted flex items-center gap-2">
          {odkudPopis()} <ArrowRight size={12} className="text-accent" /> {kamPopis()}
        </div>
        {filtrTech && <div className="px-4 pt-2.5">{filtrTech}</div>}

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="relative w-full aspect-[3/2] max-w-[300px]">
            {["top-0 left-0 border-t-[3px] border-l-[3px] rounded-tl-[14px]",
              "top-0 right-0 border-t-[3px] border-r-[3px] rounded-tr-[14px]",
              "bottom-0 left-0 border-b-[3px] border-l-[3px] rounded-bl-[14px]",
              "bottom-0 right-0 border-b-[3px] border-r-[3px] rounded-br-[14px]"].map((roh) => (
              <span key={roh} className={`absolute h-10 w-10 border-accent ${roh}`} />
            ))}
            <span className="absolute inset-x-6 top-1/2 h-[2px] bg-accent" />
          </div>
        </div>
        <p className="text-center text-[12.5px] text-ondark-muted px-6">Namiř na čárový kód položky</p>

        {/* Pás je tu kvůli vyrušení: dělník musí poznat, kde skončil. */}
        {posledni && (
          <button onClick={() => setFaze("SEZNAM")} data-testid="doklad-posledni"
            className="mx-4 mt-4 bg-white rounded-[16px] p-3 flex items-center gap-3 text-left">
            <span className="h-9 w-9 rounded-[10px] bg-[#fff6f0] text-accent font-mono font-700 text-[14px] flex items-center justify-center shrink-0">
              {radky.length}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block label-up text-sec-3 text-[9.5px]">Naposledy naskenováno</span>
              <span className="block text-[12.5px] font-600 text-ink truncate">{posledni.nazev}</span>
              <span className="block font-mono text-[10.5px] text-muted truncate">
                {[posledni.kod, posledni.ean].filter(Boolean).join(" · ") || "bez kódu"}
              </span>
            </span>
            <ArrowRight size={16} className="text-muted shrink-0" />
          </button>
        )}

        <div className="p-4 space-y-2">
          <button onClick={() => setSkenuje(true)} data-testid="doklad-skenovat"
            className="w-full h-[58px] rounded-[18px] bg-accent text-white text-[16px] font-700 flex items-center justify-center gap-2.5">
            <ScanLine size={22} /> Skenovat
          </button>
          <button onClick={() => setFaze("SEZNAM")} className="w-full text-[12.5px] text-ondark-muted">
            Zadat ručně podle názvu
          </button>
        </div>
        {chyba && <div className="px-4 pb-4 text-[12.5px] text-white/80">{chyba}</div>}

        {skenuje && (
          <SkenerSheet onClose={() => setSkenuje(false)} onKod={nactiKod}
            pauza={!!karta || !!kandidati || !!zamitnuto}
            titulek={t.nadpis} napoveda="Po potvrzení můžeš skenovat dál." />
        )}
        {karta && <KartaPolozky karta={karta} t={t} strop={strop} onZmena={setKarta}
          onPridej={pridej} onZavri={() => setKarta(null)} chyba={chyba} />}
        {kandidati && <Kandidati kandidati={kandidati} strop={strop}
          onVyber={(p) => otevriKartu(p, kandidati.kod)} onZavri={() => setKandidati(null)} />}
        {zamitnuto && <Zamitnuto zamitnuto={zamitnuto} techPopis={techPopis}
          onPridejPresto={pridejPresto} onZavri={() => setZamitnuto(null)} />}
      </div>
    );
  }

  // ---------------------------------------------------------- seznam / desktop
  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-stretch justify-center md:p-4"
      data-testid="doklad-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[720px] md:rounded-[18px] shadow-panel
        flex flex-col max-h-full md:max-h-[92vh]">
        <div className="px-5 md:px-[26px] pt-5 md:pt-6 pb-3 flex items-start gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-[17px] font-700 text-ink">{t.nadpis}</h2>
          </div>
          <button onClick={onClose} data-testid="doklad-zavrit"
            className="ml-auto h-8 w-8 rounded-full bg-line-softer text-sec flex items-center justify-center shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 md:px-[26px] space-y-4 overflow-y-auto pb-4">
          <PrepinacTypu typ={typ} onZmena={setTyp} />
          {kontext(false)}
          {upravaMista && misto}
          {filtrTech}

          {/* Skenovací zóna drží fokus — USB skener píše rovnou sem a ENTER potvrdí. */}
          <div onClick={() => skenRef.current?.focus()} data-testid="doklad-skenovaci-zona"
            className="rounded-[16px] border-2 border-dashed border-[#ffd9c2] bg-[#fff8f3] p-5 flex items-center gap-4 cursor-text">
            <span className="h-[46px] w-[46px] rounded-[14px] bg-accent text-white flex items-center justify-center shrink-0">
              <ScanLine size={24} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-700 text-ink">
                {skenText ? "Načítám kód…" : "Skener je připravený — čekám na kód"}
              </span>
              <span className="block text-[11.5px] text-sec-3 mt-0.5">
                Ruční hledání podle názvu, EAN nebo katalogového čísla je pod seznamem.
              </span>
              <input ref={skenRef} value={skenText} onChange={(e) => setSkenText(e.target.value)}
                onKeyDown={naKlavesu} autoFocus data-testid="doklad-sken-input"
                className="sr-only" aria-label="Načtený kód" />
            </span>
            <button onClick={() => setSkenuje(true)} data-testid="doklad-skenovat-kamerou"
              className="shrink-0 font-mono text-[11.5px] font-700 text-accent-hover md:hidden">
              KAMERA
            </button>
            <span className="hidden md:block font-mono text-[11.5px] font-700 text-accent shrink-0">
              {radky.length ? `${radky.length}×` : "ČEKÁM"}
            </span>
          </div>

          {seznamRadku}
          {varovaniMinus}
          {hledaciPole}
          {souhrn}
          {chyba && <div className="text-[12.5px] text-danger" data-testid="doklad-chyba">{chyba}</div>}
        </div>

        <div className="px-5 md:px-[26px] py-3.5 border-t border-line-softer flex items-center gap-2 shrink-0">
          <button onClick={() => { setRadky([]); setPosledniId(null); }} data-testid="doklad-vyprazdnit"
            className="pill border border-line-strong text-sec px-4 h-10 text-[13px] font-600">
            Vyprázdnit
          </button>
          <button onClick={() => setFaze("SKEN")} data-testid="doklad-na-sken"
            className="md:hidden pill bg-[#fff6f0] text-accent-hover px-4 h-10 text-[13px] font-700 flex items-center gap-1.5">
            <ScanLine size={16} /> Skenovat
          </button>
          <Button className="ml-auto h-10" onClick={uloz} disabled={uklada} data-testid="doklad-ulozit">
            {uklada ? <Spinner /> : <span className="md:hidden">{t.ulozit}</span>}
            {!uklada && <span className="hidden md:inline">{t.akce}</span>}
          </Button>
        </div>
      </div>

      {skenuje && (
        <SkenerSheet onClose={() => setSkenuje(false)} onKod={nactiKod}
          pauza={!!karta || !!kandidati || !!zamitnuto} titulek={t.nadpis} />
      )}
      {karta && <KartaPolozky karta={karta} t={t} strop={strop} onZmena={setKarta}
        onPridej={pridej} onZavri={() => setKarta(null)} chyba={chyba} />}
      {kandidati && <Kandidati kandidati={kandidati} strop={strop}
        onVyber={(p) => otevriKartu(p, kandidati.kod)} onZavri={() => setKandidati(null)} />}
      {zamitnuto && <Zamitnuto zamitnuto={zamitnuto} techPopis={techPopis}
        onPridejPresto={pridejPresto} onZavri={() => setZamitnuto(null)} />}
    </div>
  );
}

/** Mezikrok po skenu: co jsem načetl a kolik toho beru. Bez cen. */
function KartaPolozky({ karta, t, strop, onZmena, onPridej, onZavri, chyba }) {
  const p = karta.polozka;
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:p-4"
      data-testid="doklad-karta">
      <div className="absolute inset-0 bg-black/50" onClick={onZavri} />
      <div className="relative bg-white w-full md:max-w-[420px] rounded-t-sheet md:rounded-card shadow-panel
        px-[18px] pt-3 pb-6 md:p-6">
        <span className="md:hidden mx-auto mb-3 block h-1 w-[38px] rounded-pill bg-line-strong" />
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-700 text-ink">{p.nazev}</div>
            <div className="font-mono text-[10.5px] text-sec-3 mt-0.5">
              {[p.kod, p.ean].filter(Boolean).join(" · ") || "bez kódu"}
            </div>
          </div>
          <button onClick={onZavri} data-testid="karta-zavrit"
            className="h-8 w-8 rounded-full bg-line-softer text-sec flex items-center justify-center shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="label-up text-sec-3 mt-5 mb-2">{t.kolik}</div>
        <div className="flex items-center justify-center">
          <Stepper velky hodnota={karta.mnozstvi} testid="karta-mnozstvi"
            onZmena={(v) => onZmena({ ...karta, mnozstvi: v })} />
        </div>
        <div className="text-center font-mono text-[12px] text-muted mt-1">
          {p.mj || "ks"}
          {karta.uz ? " · položku už v seznamu máš, množství se přepíše" : ""}
        </div>
        {karta.mnozstvi > strop(p) && (
          <p className="text-[12px] text-sec-3 mt-3 flex items-start gap-2">
            <span className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0" />
            Evidováno je {strop(p)} {p.mj || "ks"}. Uložit to půjde, doklad se označí.
          </p>
        )}
        {karta.pridatKod && (
          <p className="text-[11.5px] text-muted mt-2">
            Kód {karta.pridatKod} se k položce uloží, příště se načte sám.
          </p>
        )}
        {chyba && <p className="text-[12.5px] text-danger mt-2">{chyba}</p>}

        <button onClick={onPridej} data-testid="karta-pridat"
          className="mt-5 w-full h-14 rounded-[16px] bg-accent text-white text-[15px] font-700">
          Přidat do seznamu
        </button>
        <button onClick={onZavri} className="mt-2 w-full text-[12.5px] text-muted">Zrušit</button>
      </div>
    </div>
  );
}

/**
 * Kód sedí, ale položka neprošla filtrem. Bez vysvětlení by to vypadalo
 * jako rozbitý skener, tak řekneme důvod a u měkkého filtru necháme projít.
 */
function Zamitnuto({ zamitnuto, techPopis, onPridejPresto, onZavri }) {
  const p = zamitnuto.polozka || {};
  const mekky = zamitnuto.duvod === "jina_technologie";
  const titulek = {
    nevydano: "Tahle položka se na zakázku nevydala",
    neni_skladem: "Na zdrojovém skladu tahle položka neleží",
    jina_technologie: "Položka nepatří k vybrané technologii",
  }[zamitnuto.duvod] || "Položku sem přidat nejde";
  const popis = {
    nevydano: "Vrátit jde jen materiál, který z výdejky na zakázku odešel. Zkontroluj zakázku a technologii v hlavičce dokladu.",
    neni_skladem: "Převádět jde jen to, co na skladu opravdu je. Nejdřív udělej příjem, nebo převáděj z jiného skladu.",
    jina_technologie: `Podle ceníku nesedí k technologii ${techPopis || "vybrané v hlavičce"}. Když to tam patří, dej ji do seznamu i tak.`,
  }[zamitnuto.duvod] || "";

  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:p-4"
      data-testid="doklad-zamitnuto">
      <div className="absolute inset-0 bg-black/50" onClick={onZavri} />
      <div className="relative bg-white w-full md:max-w-[420px] rounded-t-sheet md:rounded-card shadow-panel
        px-[18px] pt-3 pb-6 md:p-6 space-y-3">
        <span className="md:hidden mx-auto mb-1 block h-1 w-[38px] rounded-pill bg-line-strong" />
        <div className="flex items-start gap-3">
          <span className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block t-sekce text-ink">{titulek}</span>
            <span className="block t-meta text-muted mt-1">{popis}</span>
          </span>
        </div>
        <div className="rounded-card border border-line-softer px-3.5 py-3">
          <div className="t-telo font-600 text-ink truncate">{p.nazev || "Položka"}</div>
          <div className="font-mono t-meta text-muted truncate mt-0.5">
            {[p.kod, p.ean].filter(Boolean).join(" · ") || "bez kódu"}
            {p.dostupne != null ? ` · skladem ${p.dostupne} ${p.mj || "ks"}` : ""}
          </div>
        </div>
        {mekky && (
          <button onClick={onPridejPresto} data-testid="zamitnuto-presto"
            className="tap w-full h-12 rounded-[16px] bg-accent text-white t-telo font-700">
            Přidat i tak
          </button>
        )}
        <button onClick={onZavri} data-testid="zamitnuto-zavrit"
          className="tap w-full h-11 pill border border-line-strong t-telo font-600 text-sec">
          Skenovat dál
        </button>
      </div>
    </div>
  );
}

/** Neznámý kód: k čemu ho přiřadit, ať se příště načte sám. */
function Kandidati({ kandidati, strop, onVyber, onZavri }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:p-4"
      data-testid="doklad-kandidati">
      <div className="absolute inset-0 bg-black/50" onClick={onZavri} />
      <div className="relative bg-white w-full md:max-w-[460px] rounded-t-sheet md:rounded-card shadow-panel
        p-5 space-y-3 max-h-[80vh] overflow-y-auto">
        <div className="text-[15px] font-800 text-ink">Kód {kandidati.kod} neznáme</div>
        <p className="text-[12.5px] text-muted">
          Vyber položku, ke které kód patří. Uloží se k ní a příště ji skener najde sám.
        </p>
        {(kandidati.items || []).map((p) => (
          <button key={p.id} type="button" onClick={() => onVyber(p)} data-testid={`doklad-kandidat-${p.id}`}
            className="w-full text-left flex items-center gap-3 px-3.5 py-3 border border-line-softer rounded-card hover:bg-inputbg">
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-600 text-ink truncate">{p.nazev}</span>
              <span className="block font-mono text-[11px] text-muted truncate">
                {[p.kod, p.ean].filter(Boolean).join(" · ") || "bez kódu"}
              </span>
            </span>
            <span className="font-mono text-[12px] font-700 text-ink shrink-0">{strop(p)} {p.mj || "ks"}</span>
          </button>
        ))}
        {!(kandidati.items || []).length && (
          <p className="text-[12.5px] text-muted">Nic podobného na skladu není. Zkus hledat podle názvu.</p>
        )}
        <button onClick={onZavri} className="w-full pill border border-line-strong h-11 text-[13px] font-600 text-sec">
          Zavřít
        </button>
      </div>
    </div>
  );
}
